import SearchIcon from '@mui/icons-material/Search';
import React, { useCallback, useEffect } from 'react';
import { Control, useFormContext } from 'react-hook-form';
import { debounce } from 'utils/debounce';
import { useStoreSelector } from 'v3/data/store';
import { showErrorDialog } from 'v3/data/store/error/actions';
import { showSnackbar } from 'v3/data/store/snackbar/actions';
import { setStoreGroupList, setStoreGroupSelected } from 'v3/data/store/stores/actions';
import { IStoreGroup } from 'v3/data/store/stores/types';
import { getAllStoreGroups, getJazzStoreData } from 'v3/services/api';
import { FormValues } from 'v3/shared/components/FieldStore/types';
import { DropdownSelectFormController } from 'v3/shared/components/Inputs/DropdownSelectFormController';
import { InputFormPassword } from 'v3/shared/components/Inputs/InputFormPassword';
import { SubtitleLarge } from 'v3/shared/typography/SubtitleLarge';
import { TitleSmall } from 'v3/shared/typography/TitleSmall';
import { theme } from 'v3/styles/global-styles';
import { InputFormController } from '../../Inputs/InputFormController';
import { TextMaskCustom } from '../../Inputs/MaskCustomInput';
import { RadioFormController } from '../../Inputs/RadioFormController';
import { IconHeader } from '../IconHeader';
import { ContainerStores, Content, Header } from '../styles';

interface StoreIdentifierProps {
  control: Control<FormValues>;
  onStoreDataChange: (data: any) => void;
}

export const IsMotorola = {
  Sim: true,
  Não: false,
} as const;

export const StoreIdentifier: React.FC<StoreIdentifierProps> = ({ control, onStoreDataChange }) => {
  const { setValue, watch } = useFormContext<FormValues>();
  const jazzStoreIdValue = watch('jazzStoreId');
  const { storeGroupList } = useStoreSelector((store) => store.store.storeGroup);

  const storeGroupsFormatter = (storeGroups: IStoreGroup[]) => {
    return storeGroups.map((item) => ({ label: item.name, value: item.id }));
  };
  const DEBOUNCE_DELAY = 1000;

  const handleJazzStoreIdChange = async (jazzStoreId: string) => {
    try {
      const response = await getJazzStoreData(jazzStoreId);
      const storeData = response.data;
      setValue('cnpj', storeData.cnpj);
      setValue('email', storeData.email);
      onStoreDataChange(storeData);
    } catch (error) {
      console.error('Erro ao obter dados da Jazz Store:', error);
      showSnackbar('JazzStoreID inválido. Revise o campo ou insira um ID válido.', 'error', true);
      setValue('cnpj', '');
      setValue('email', '');
      onStoreDataChange({});
    }
  };

  const debouncedHandleJazzStoreIdChange = (jazzStoreId: string) => {
    debounce(() => handleJazzStoreIdChange(jazzStoreId), DEBOUNCE_DELAY, 'handleJazzStoreIdChange');
  };
  const fetchStoreGroups = useCallback(async () => {
    try {
      const response = await getAllStoreGroups();
      setStoreGroupList(response.data);
    } catch (error) {
      showErrorDialog('Erro ao obter lista de grupos', error);
    }
  }, []);

  useEffect(() => {
    if (jazzStoreIdValue) {
      debouncedHandleJazzStoreIdChange(jazzStoreIdValue);
    }
  }, [jazzStoreIdValue]);

  useEffect(() => {
    fetchStoreGroups();
  }, [fetchStoreGroups]);

  return (
    <ContainerStores>
      <Header>
        <IconHeader Icon={SearchIcon} />
        <SubtitleLarge>Identificador da loja</SubtitleLarge>
      </Header>
      <Content>
        <InputFormController
          data-testid="input-jazzStoreId"
          formControl={control}
          formControlName="jazzStoreId"
          label="JazzStoreID"
        />

        <InputFormController
          data-testid="input-cnpj"
          formControl={control}
          formControlName="cnpj"
          label="CNPJ"
          mask="00.000.000/0000-00"
          customMask={TextMaskCustom}
        />

        <InputFormController
          data-testid="input-email"
          formControl={control}
          formControlName="email"
          label="E-mail (Login da loja)"
        />

        <InputFormPassword
          data-testid="input-password"
          formControl={control}
          formControlName="password"
          label="Senha"
        />

        <InputFormPassword
          data-testid="input-confirmPassword"
          formControl={control}
          formControlName="confirmPassword"
          label="Confirmar senha"
        />

        <DropdownSelectFormController
          formControl={control}
          formControlName="storeGroupId"
          selectValues={storeGroupsFormatter(storeGroupList)}
          onChangeSelect={(id: string) => {
            setStoreGroupSelected({ id });
          }}
          label="Grupo"
        />

        <TitleSmall color={theme.palette.secondary.light}>É uma loja motorola?</TitleSmall>
        <RadioFormController formControl={control} formControlName="isMotorola" label="" defaultValues={IsMotorola} />
      </Content>
    </ContainerStores>
  );
};
